<template>
  <div
    class="asset-module"
    :class="{
      'asset-module--channel': type === 'Channel',
      'asset-module--horizontal': layout === 'left',
      'asset-module--vertical': layout === 'top',
      'asset-module--no-text': layout === 'image',
      'asset-module--layout-overridden': layoutOverride,
      'asset-module--transparent': useTransparentBackground,
    }"
  >
    <div
      class="asset-module__error-message"
      v-if="error"
    >
      {{ error }}
    </div>
    <button
      v-if="layoutOverride"
      :style="{
        transform: `translateZ(0) scale(${1 / this.scale})`,
      }"
      class="asset-module__cancel-layout-override comet-button"
      :aria-label="$t('Collapse Video')"
      @click="cancelLayoutOverride"
    >
      <NebulaIcon
        symbol-id="x"
        class="asset-module__cancel-layout-override-icon"
      />
    </button>
    <NebulaChannelCard
      v-if="useChannelCardLayout"
      :asset="asset"
      class="asset-module__channel-card"
      :class="{ 'asset-module__channel-card--edit': mode === 'EDIT' }"
      element="div"
      :target="'_blank'"
      :use-link="mode === 'VIEW'"
      :url="`${domains.appDomain}/player/${asset.id}`"
    >
      <template #actionsMenu>
        <ModuleActionMenu
          v-if="user"
          :asset="asset"
          :module-data="moduleData"
          :scale="scale"
          :canvas-name="canvasName"
          :is-displayed="isDisplayed"
        />
      </template>
    </NebulaChannelCard>
    <ImageViewer
      v-else-if="assetGroup === 'image'"
      :active="active"
      :allow-zoom="moduleData.options.allow_zoom"
      :mode="mode"
      :url="thumbnailUrl"
      :title="title"
      :image-settings="imageSettings"
      :use-transparent-background="useTransparentBackground"
      :module-data="moduleData"
      :scale="scale"
      :canvas-name="canvasName"
      :theme-name="themeName"
    />
    <VideoViewer
      v-else-if="assetGroup === 'video'"
      ref="videoViewer"
      :video="video"
      :autoplay="autoplay"
      :loop="loop"
      :start-time="startTime"
      :stop-time="stopTime"
      :thumbnail-url="thumbnailUrl"
      :image-settings="imageSettings"
      :scale="scale"
      :use-transparent-background="useTransparentBackground"
      :theme-name="themeName"
      :mode="mode"
      :module-theme="moduleTheme"
      :player-link="playerLink"
      :canvas-name="canvasName"
      :asset-id="assetId"
      @play="onVideoPlay"
      @pause="onVideoPause"
      @ended="onVideoEnded"
    />
    <AudioViewer
      v-else-if="assetGroup === 'audio'"
      ref="audioViewer"
      :id="moduleData.id"
      :autoplay="autoplay"
      :image-settings="imageSettings"
      :scale="scale"
      :url="mediaUrl"
    />
    <PlayerViewer
      v-else
      :asset="asset"
      :url="thumbnailUrl"
      :mode="mode"
      :player-link="playerLink"
      :title="title"
      :image-settings="imageSettings"
      :type="type"
      :use-transparent-background="useTransparentBackground"
      :scale="scale"
      :is-displayed="isDisplayed"
    />
    <div
      class="nebula-card__content"
      v-if="layout !== 'image' && !useChannelCardLayout"
    >
      <div
        class="nebula-card__header"
        v-if="showType"
      >
        <p class="nebula-card__type">
          {{ friendlyAssetType }}
        </p>
        <!-- These props and listeners come from the actionMenu mixin -->
        <ModuleActionMenu
          v-if="user"
          :asset="asset"
          :module-data="moduleData"
          :scale="scale"
          :canvas-name="canvasName"
          :is-displayed="isDisplayed"
        />
      </div>
      <template v-if="showTitle">
        <!-- The referrerpolicy here is to allow better url tracking -->
        <a
          v-if="mode !== 'EDIT' && playerLink"
          :href="playerLink"
          target="_blank"
          referrerpolicy="no-referrer-when-downgrade"
          class="nebula-card__title"
        >
          {{ decodedTitle }}
        </a>
        <span
          v-else
          class="nebula-card__title asset-module__disabled-title"
        >
          {{ decodedTitle }}
        </span>
      </template>
      <p
        class="nebula-card__description"
        v-if="showDescription"
      >
        {{ decodedDescription }}
      </p>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { NebulaChannelCard, NebulaIcon } from '@discoveryedu/nebula-components';
import { mapState } from 'pinia';
import { bus } from '@/lib/eventBus';
import PlayerViewer from '@/components/media/PlayerViewer.vue';
import ImageViewer from '@/components/media/ImageViewer.vue';
import { decodeHTML, stripTags } from '@/lib/utils';
import ModuleActionMenu from '@/components/ModuleActionMenu.vue';
import {
  useAssetStore,
  useUserStore,
} from '@/stores';

// These are also loaded manually when asset data is cached, so they should be available by
// the time they need to be rendered
const VideoViewer = defineAsyncComponent(() => import('@/components/media/VideoViewer.vue'));
const AudioViewer = defineAsyncComponent(() => import('@/components/media/AudioViewer.vue'));

export default {
  name: 'MediaViewer',
  components: {
    AudioViewer,
    VideoViewer,
    ImageViewer,
    ModuleActionMenu,
    NebulaChannelCard,
    NebulaIcon,
    PlayerViewer,
  },
  props: {
    assetGroup: {
      type: String,
      default: null,
    },
    assetId: {
      type: String,
      default: null,
    },
    autoplay: Boolean,
    loop: {
      type: Boolean,
      default: false,
    },
    description: {
      type: String,
      default: null,
    },
    mediaUrl: {
      type: String,
      default: null,
    },
    mode: {
      type: String,
      default: null,
    },
    playerLink: {
      type: String,
      default: null,
    },
    thumbnailUrl: {
      type: Object,
      default() {
        return {};
      },
    },
    scale: {
      type: Number,
      default: 1,
    },
    title: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    startTime: {
      type: Number,
      default: null,
    },
    stopTime: {
      type: Number,
      default: null,
    },
    moduleData: {
      type: Object,
      default() {
        return {
          options: {
            allow_zoom: false,
            image_settings: 'letterbox',
            layout: 'image',
          },
        };
      },
    },
    iconId: {
      type: String,
      default: 'asset-document',
    },
    showTitle: Boolean,
    showDescription: Boolean,
    showType: Boolean,
    useTransparentBackground: {
      type: Boolean,
      default: false,
    },
    video: {
      type: Object,
      default: null,
    },
    active: {
      type: Boolean,
      default: false,
    },
    canvasName: {
      type: String,
      default: null,
    },
    isDisplayed: {
      type: Boolean,
      default: true,
    },
    themeName: {
      type: String,
      default: 'default',
    },
  },
  emits: [
    'video-pause',
    'video-play',
    'video-ended',
  ],
  data() {
    return {
      error: null,
      layoutOverride: null,
    };
  },
  computed: {
    ...mapState(useAssetStore, [
      'assets',
      'domains',
    ]),
    ...mapState(useUserStore, [
      'user',
    ]),
    asset() {
      return this.assets[this.assetId];
    },
    decodedDescription() {
      // Asset desciptions can have both HTML tags like <p> and HTML entities like &amp;
      return decodeHTML(stripTags(this.description));
    },
    decodedTitle() {
      // Asset titles can have both HTML tags like <p> and HTML entities like &amp;
      return decodeHTML(stripTags(this.title));
    },
    friendlyAssetType() {
      if (this.type === 'Other') return this.$t('Document');

      return this.$t(this.type);
    },
    imageSettings() {
      return this.moduleData.options.image_settings;
    },
    moduleTheme() {
      return this.moduleData.options?.theme || {};
    },
    showText() {
      return this.showTitle || this.showDescription || this.showType;
    },
    layout() {
      // If the layout has been overriden, then use the override.
      if (this.layoutOverride) return this.layoutOverride;
      /*
        If there is no text displayed in the UI, use the image only layout,
        otherwise image or video content might be cropped by module boundaries.
      */
      if (!this.showText) return 'image';
      return this.moduleData.options.layout;
    },
    useChannelCardLayout() {
      return this.type === 'Channel' && this.moduleData.options.layout === 'top';
    },
  },
  watch: {
    layoutOverride() {
      /*
        When the video is expanded to the full size of the module, disable dragging
        of the module, otherwise enable dragging.
      */
      bus.emit('module:set-is-draggable', {
        moduleId: this.moduleData.id,
        isDraggable: this.layoutOverride !== 'image',
      });
    },
    'moduleData.options.layout': function handler() {
      this.toggleLayoutOverride();
    },
    showText() {
      this.toggleLayoutOverride();
    },
  },
  methods: {
    toggleLayoutOverride() {
      /*
        If we're in text-beside mode with text showing and a video is playing,
        expand the video to the full size of the module by overriding with
        the 'image' layout.
      */
      const videoIsPlaying = !!this.$refs.videoViewer && !this.$refs.videoViewer.paused();
      if (this.moduleData.options.layout === 'left' && this.showText && videoIsPlaying) {
        this.layoutOverride = 'image';
      } else {
        this.layoutOverride = null;
      }
    },
    onVideoPause() {
      this.$emit('video-pause');
    },
    onVideoPlay() {
      this.toggleLayoutOverride();
      this.$emit('video-play');
    },
    onVideoEnded() {
      this.$emit('video-ended');
    },
    cancelLayoutOverride() {
      this.layoutOverride = null;
      this.$refs.videoViewer.pause();
    },
    pause() {
      const videoViewer = this.$refs?.videoViewer;
      if (videoViewer) {
        videoViewer.pause();
      }
      const audioViewer = this.$refs?.audioViewer;
      if (audioViewer) {
        audioViewer.pause();
      }
    },
    reset() {
      const videoViewer = this.$refs?.videoViewer;
      if (videoViewer) {
        videoViewer.reset();
      }
      const audioViewer = this.$refs?.audioViewer;
      if (audioViewer) {
        audioViewer.reset();
      }
    },
  },
};

</script>

<style lang="stylus">
.asset-module {
  background-color: $nebula-color-white;

  &--transparent {
    background-color: transparent;
  }

  &.asset-module--layout-overridden {
    position: relative;
  }

  .asset-module__cancel-layout-override-icon {
    height: 14px;
    width: 14px;
  }

  .asset-module__cancel-layout-override {
    align-items: center;
    background: rgba(43, 51, 63, 0.7); // matches color on videojs video player toolbar
    border-width: 0;
    border-radius: 0 $nebula-border-radius-badge-default 0 0;
    display: flex;
    fill: $nebula-color-white;
    height: 26px;
    justify-content: center;
    margin: 0;
    opacity: 1.0;
    padding: 0;
    position: absolute;
    right: 0;
    top: 0;
    transform-origin: right top;
    width: 26px;
    z-index: 1;

    &:before {
      margin: 0;
    }
  }

  &__channel-card {
    border-radius: 6px; // match other canvas modules
    height: 100%;
    margin: 0;
    max-width: 100%;
    min-width: 100%;
    width: 100%;

    .nebula-card__thumbnail-container {
      border-radius: 6px; // match other canvas modules
    }

    .nebula-card__content-placeholder {
      border-radius: 0 0 6px 6px; // match other canvas modules
    }

    &--edit .nebula-card__title:hover {
      color: inherit;
    }
  }

  .asset-module__video-player {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  .asset-module__image--transparent {
    background-color: transparent;
  }

  .asset-module__disabled-title {
    &:hover, &:focus {
      color: inherit;
    }
  }

  &.asset-module--no-text {
    height: 100%;

    .asset-module__image {
      height: 100%;
      padding-top: 0; // image-only fills are the dimensions of the module rather than 16:9
    }
  }

  &.asset-module--vertical {
    .asset-module__image {
      border-end-start-radius: 0;
      border-end-end-radius: 0;
    }
  }

  &.asset-module--channel {
    height: 100%;
  }

  &.asset-module--horizontal {
    display: flex;
    height: 100%;

    > div {
      height: 100%;
    }

    .asset-module__image,
    .image-viewer {
      border-end-end-radius: 0;
      border-start-end-radius: 0;
      min-width: 20%;

      .asset-module__image {
        width: 100%;
      }
    }

    .comet-thumbnail,
    .image-viewer {
      height: 100%;
      max-width: 40%;
      padding-top: 0;

      .asset-module__image {
        max-width: 100%;
      }
    }

    .nebula-card__content {
      flex-grow: 1;
      overflow: auto;
    }
  }

  .comet-thumbnail:not(.comet-thumbnail--contain) {
    background-color: $nebula-color-interface-blue-400;

    &.asset-module__image--transparent {
      background-color: transparent;
    }
  }

  .nebula-card__title,
  .nebula-card__description {
    /*
      This padding allows highlight selection indicator to be visible
      if selected highlight is on the bottom row of text.
    */
    padding-bottom: 2px;

    .de-note {
      inset-inline-start: -0.2em;
      top: -0.05em;
    }
  }
}
</style>
